import AdminLayout from "../../commponents/layout/adminLayout";
import { useEffect, useState } from "react";
import type { TableColumn } from "../../commponents/table/types";
import { Download, Eye, Pencil, Plus, Trash2 } from "lucide-react";

import {
  deleteDeliveryOrderById,
  deliveryOrderPdf,
  getDeliveryOrder,
  getdeliveryOrderStatus,
} from "../../api/deliveryOrderApi";
import { useNavigate } from "react-router-dom";
import DeliveryOrderPdfPreviewModal from "../../commponents/deliveryOrder/DeliveryOrderPdfPreviewModal";
import DataTable from "../../commponents/table/DataTable";
import StatusFilter from "../../commponents/filter/StatusFilter";
import { showConfirm, showError, showSuccess } from "../../utils/ alert";

interface DeliveryOrderStatus {
  id: number;
  name: string;
  code: string;
  color: string;
}

interface DeliveryOrder {
  id: number;
  delivery_order_no: string;
  bill_to: string;
  delivery_date: string;

  status: DeliveryOrderStatus | null;

  createdAt: string;
  updatedAt: string;
}

export interface status {
  id: number;
  name: string;
  code: string;
  color: string;
}

export default function DeliveryOrderList() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deliveryOrder, setDeliveryOrder] = useState<DeliveryOrder[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // ---- State modal preview PDF ----
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFilename, setPreviewFilename] = useState("delivery-order.pdf");
  const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
     const [statusOptions, setStatusOptions] = useState<status[]>([]);

  const navigate = useNavigate();

  const columns: TableColumn<DeliveryOrder>[] = [
    { key: "delivery_date", title: "Date" },
    { key: "bill_to", title: "To" },
    { key: "delivery_order_no", title: "No" },
    {
      key: "status",
      title: "Status",
      render: (row) => (
        <span
          className="font-medium"
          style={{
            color: row.status?.color ?? "#6b7280",
          }}>
          {row.status?.name ?? "-"}
        </span>
      ),
    },

    {
      key: "id",
      title: " ",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.status?.code === "SHIPPED" && (
            <button
              onClick={() => handleCreateInvoice(row.id)}
              className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 hover:border-blue-700 border-2 border-blue-100 rounded-lg p-1">
              <Plus size={16} />
              Invoice
            </button>
          )}
        </div>
      ),
    },
    {
      key: "id",
      title: "Action",
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleDetail(row.id)}
            className="rounded-lg bg-blue-100 p-2 text-blue-600 hover:bg-blue-200">
            <Eye size={18} />
          </button>

          <button
            onClick={() => handlePreviewPdf(row)}
            className="rounded-lg bg-blue-100 p-2 text-blue-600 hover:bg-blue-200">
            <Download size={18} />
          </button>

          <button
            onClick={() => handleEdit(row.id)}
            className="rounded-lg bg-yellow-100 p-2 text-yellow-600 hover:bg-yellow-200">
            <Pencil size={18} />
          </button>

          <button
            onClick={() => handleDelete(row.id)}
            className="rounded-lg bg-red-100 p-2 text-red-600 hover:bg-red-200">
            <Trash2 size={18} />
          </button>
        </div>
      ),
    },
  ];

  function handleCreateInvoice(id: number): void {
    navigate(`/invoice/add/${id}`);
  }
  

  const fetchDeliveryOrder = async () => {
    try {
      setLoading(true);
      const response = await getDeliveryOrder({
        page: currentPage,
        limit: pageSize,
        search,
        status
      });

      setDeliveryOrder(response.data.deliveryOrderList);
      setTotalPages(response.data.pagination.totalPages);

      const dataStatus = await getdeliveryOrderStatus()
   
      setStatusOptions(dataStatus.data.deliveryOrderList);
      
    } catch (error) {
         showError("Failed", "Failed to Load delivery Order List.");
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    fetchDeliveryOrder();
  }, [currentPage, pageSize, search, status]);

  // Cleanup object URL kalau komponen unmount saat modal masih terbuka
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleDetail = (id: number) => {
    navigate(`/delivery_order/detail/${id}`);
  };

  const handleEdit = (id: number) => {
    navigate(`/delivery_order/edit/${id}`);
  };

  const handlePreviewPdf = async (row: DeliveryOrder) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl(null);
    setPreviewFilename(`${row.delivery_order_no || "delivery-order"}.pdf`);

    try {
      const response = await deliveryOrderPdf(row.id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {

      showError("Failed to load delivery order preview.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  const handleDelete = async (id: number) => {
    try {
      const confirmed = await showConfirm(
        "Delete Invoice",
        "Are you sure you want to delete this invoice?"
      );
      if (!confirmed) return;

      await deleteDeliveryOrderById(id);
      await fetchDeliveryOrder();

      await showSuccess("Delivery Order deleted successfully.");
    } catch (error) {

      showError("Failed to delete Delivery Order.");
    }
  };

  const handleAdd = () => navigate("/delivery_order/add");

  return (
    <AdminLayout>
      <div className="rounded-3xl bg-white p-6">
        <DataTable
          title="Delivery Order List"
          columns={columns}
          data={deliveryOrder}
          onAdd={handleAdd}
          filterContent={
                                <>
                                  <StatusFilter
                                    value={status}
                                    onChange={setStatus}
                                    options={statusOptions.map((data) => {
                                      return {
                                        label: data.name,
                                        value: data.code,
                                      };
                                    })}
                                  />
                                </>
                              }
          onSearch={(value) => {
            // FIX: reset ke halaman 1 tiap kali keyword search berubah,
            // supaya tidak "nyangkut" di halaman kosong (mis. lagi di
            // halaman 3 lalu ngetik keyword baru yang hasilnya cuma 1 halaman).
            setSearch(value);
            setCurrentPage(1);
          }}
          pagination={{
            currentPage,
            totalPages,
            pageSize,
            onPageChange: setCurrentPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
          }}
        />

        <DeliveryOrderPdfPreviewModal
          open={previewOpen}
          loading={previewLoading}
          pdfUrl={previewUrl}
          filename={previewFilename}
          onClose={handleClosePreview}
        />
      </div>
    </AdminLayout>
  );
}
